/**
 * BookingKpiStrip — the week-scoped KPI tiles (BON-130).
 *
 * Hidden on mobile; on tablet/desktop the responsive grid places five tiles
 * (Bookings · Tickets · Revenue · Refund rate · Rebook rate). Values come from
 * the stats client; while stats are loading/errored they render as em dashes
 * rather than disappearing. Deltas are green when non-negative.
 */
import type { BookingStats } from '../../api/bookingsList';

import { formatCompactMoney, formatDeltaPct, isPositiveDelta } from './bookingListModel';

const LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-stone-500';
const VALUE = 'mt-1 text-2xl font-bold text-bark-900 dark:text-[#e8ece9]';
const SUB = 'mt-1 text-xs text-stone-500';

function Tile({
  label,
  value,
  children,
  className = '',
}: {
  label: string;
  value: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-stone-200 bg-white p-4 dark:border-stone-700 dark:bg-[#1a1f1c] ${className}`}
    >
      <p className={LABEL}>{label}</p>
      <p className={VALUE}>{value}</p>
      <p className={SUB}>{children}</p>
    </div>
  );
}

function Delta({ pct }: { pct: number | null }) {
  const positive = isPositiveDelta(pct);
  return (
    <>
      <span
        className={
          pct === null
            ? 'text-stone-500'
            : positive
              ? 'font-medium text-moss-600 dark:text-moss-400'
              : 'font-medium text-clay-600 dark:text-clay-400'
        }
      >
        {formatDeltaPct(pct)}
      </span>{' '}
      vs last
    </>
  );
}

const DASH = '—';

/** Resolves every tile's display strings up front so the component render stays
 *  branch-free (dashes when stats are still loading / errored). */
function kpiValues(stats: BookingStats | null) {
  if (!stats) {
    return {
      bookings: DASH,
      tickets: DASH,
      ticketsSub: DASH,
      revenue: DASH,
      refund: DASH,
      refundSub: DASH,
      rebook: DASH,
      rebookSub: DASH,
    };
  }
  return {
    bookings: String(stats.bookingsThisWeek),
    tickets: String(stats.ticketsSold),
    ticketsSub: `${Math.round(stats.ticketsOnlinePct)}% online`,
    revenue: formatCompactMoney(stats.revenueCents),
    refund: `${stats.refundRatePct.toFixed(1)}%`,
    refundSub: `${stats.refundCount} this wk`,
    rebook: `${stats.rebookRatePct.toFixed(1)}%`,
    rebookSub: `${stats.rebookCount} this wk`,
  };
}

export function BookingKpiStrip({ stats }: { stats: BookingStats | null }) {
  const v = kpiValues(stats);
  return (
    <div className="mb-6 hidden grid-cols-4 gap-4 md:grid lg:grid-cols-5">
      <Tile label="Bookings · wk" value={v.bookings}>
        <Delta pct={stats?.bookingsDeltaPct ?? null} />
      </Tile>
      <Tile label="Tickets sold" value={v.tickets}>
        {v.ticketsSub}
      </Tile>
      <Tile label="Revenue · wk" value={v.revenue}>
        <Delta pct={stats?.revenueDeltaPct ?? null} />
      </Tile>
      <Tile label="Refund rate" value={v.refund}>
        {v.refundSub}
      </Tile>
      <Tile label="Rebook rate" value={v.rebook}>
        {v.rebookSub}
      </Tile>
    </div>
  );
}
